import React, { useId, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Modal } from 'reactstrap';
import type { DateRedoItem } from 'fym-shared/src/music24/dateRedo';
import { TypedMemo } from '../../../../../common/types';
import { LoadingView } from '../../../../../common/views/LoadingView';
import { SMLButton } from '../../../../components/primitives/SMLButton';
import { studioThemeMessages } from '../../../studio/studioThemeMessages';
import { CoverRedoPosts } from '../coverRedo/CoverRedoPosts';
import { coverRedoMessages } from '../coverRedo/coverRedoMessages';
import type { DateRedoDialogModel } from './useDateRedoDialog';
import { BeforeReleaseGroup } from './BeforeReleaseGroup';
import { DateRedoItemLine } from './DateRedoItemLine';
import { dateRedoMessages as messages } from './dateRedoMessages';
/* eslint-disable css-modules/no-unused-class -- the stylesheet of the cover redo; the dialog uses its dialog slice */
import styles from '../coverRedo/CoverRedo.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** One file that the redo leaves out, with its reason (the before-release files have their own group). */
const SkipLine: React.FC<{ item: DateRedoItem }> = ({ item }) => {
  const intl = useIntl();
  const name = intl.formatMessage(studioThemeMessages[item.themeId].name);
  let reason: string;
  switch (item.skipReason) {
    case 'coverRedo':
      reason = intl.formatMessage(messages.skipCoverRedo, { name });
      break;
    case 'busy':
      reason = intl.formatMessage(messages.skipBusy, { name });
      break;
    case 'needsMaster':
      reason = `${name} · ${intl.formatMessage(coverRedoMessages.skipNeedsMaster)}`;
      break;
    case 'masterProcessing':
      reason = `${name} · ${intl.formatMessage(coverRedoMessages.skipMasterProcessing)}`;
      break;
    case 'failed':
    case 'beforeRelease':
    case null:
      reason = intl.formatMessage(coverRedoMessages.skipFailed, { name });
      break;
  }
  return (
    <div className={styles.skip}>
      <span>{reason}</span>
    </div>
  );
};

/**
 * "Fix the release date": the files that are made again with old → new, the files that are left
 * out, the "Before release" group, the posts that move (the post part of the cover redo) and the
 * confirm. A bottom sheet below 576 px. The redo is free.
 */
const DateRedoDialogComponent: React.FC<{ model: DateRedoDialogModel }> = ({ model }) => {
  const intl = useIntl();
  const titleId = useId();
  const { state } = model;
  const { toMake, skipped, beforeRelease } = useMemo(() => {
    const items = state?.items ?? [];
    return {
      toMake: items.filter((item) => item.skipReason === null),
      skipped: items.filter((item) => item.skipReason !== null && item.skipReason !== 'beforeRelease'),
      beforeRelease: items.filter((item) => item.skipReason === 'beforeRelease'),
    };
  }, [state]);
  return (
    <Modal
      isOpen={model.isOpen}
      toggle={model.close}
      centered
      labelledBy={titleId}
      className={styles.dialog}
      contentClassName={styles.content}
      returnFocusAfterClose
    >
      <span className={styles.grab} aria-hidden="true" />
      <div className={styles.head} data-testid="date-redo-dialog">
        <div className={styles.headText}>
          <h3 id={titleId}>{intl.formatMessage(messages.dialogTitle)}</h3>
          <p>{intl.formatMessage(messages.dialogLead)}</p>
        </div>
      </div>
      {state === undefined ? (
        <div className={styles.loading}>{model.failed ? null : <LoadingView />}</div>
      ) : (
        <>
          <div className={styles.section}>
            <div className={styles.eyebrow}>
              <span>{intl.formatMessage(coverRedoMessages.whatWeMake)}</span>
              <span>{intl.formatMessage(coverRedoMessages.fileCount, { count: toMake.length })}</span>
            </div>
            {toMake.map((item) => (
              <DateRedoItemLine key={item.uuid} item={item} newDateLabel={state.newDateLabel} />
            ))}
            {skipped.map((item) => (
              <SkipLine key={item.uuid} item={item} />
            ))}
          </div>
          {beforeRelease.length > 0 && (
            <div className={styles.section}>
              <BeforeReleaseGroup items={beforeRelease} newDateLabel={state.newDateLabel} />
            </div>
          )}
          <CoverRedoPosts model={model} />
        </>
      )}
      {model.failed && (
        <p className={styles.error} role="alert" data-testid="date-redo-error">
          {intl.formatMessage(coverRedoMessages.errorOther)}
        </p>
      )}
      <div className={styles.footer}>
        <div className={styles.total}>
          <b>{intl.formatMessage(messages.free)}</b>
          <small>{intl.formatMessage(messages.freeNote)}</small>
        </div>
        <SMLButton onClick={model.close} disabled={model.isStarting}>
          {intl.formatMessage(coverRedoMessages.cancel)}
        </SMLButton>
        <SMLButton
          variant="primary"
          onClick={model.confirm}
          disabled={state === undefined || model.count === 0 || model.isStarting}
        >
          {intl.formatMessage(messages.redo, { count: model.count })}
        </SMLButton>
      </div>
    </Modal>
  );
};

export const DateRedoDialog = TypedMemo(DateRedoDialogComponent);
